import type { WorldDefinition } from '@sw/schema'

const ACCESS_TOKEN_KEY = 'sw.accessToken'
const REFRESH_TOKEN_KEY = 'sw.refreshToken'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code = 'error',
  ) {
    super(message)
  }
}

export const tokens = {
  get access(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },
  get refresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },
  set(access: string, refresh?: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) }
  const token = tokens.access
  if (token) headers.authorization = `Bearer ${token}`

  let payload: BodyInit | undefined
  if (body instanceof ArrayBuffer || body instanceof Blob) {
    payload = body as BodyInit
  } else if (body !== undefined) {
    headers['content-type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  const response = await fetch(path, { ...init, method, headers, body: payload })

  if (response.status === 401 && tokens.refresh && path !== '/v1/auth/refresh') {
    // One quiet retry after refreshing, so a fifteen-minute token expiring
    // mid-tour is invisible.
    const refreshed = await tryRefresh()
    if (refreshed) return request<T>(method, path, body, init)
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const parsed = text ? (JSON.parse(text) as unknown) : undefined

  if (!response.ok) {
    const error = parsed as { message?: string; error?: string } | undefined
    throw new ApiError(
      response.status,
      error?.message ?? 'Something went wrong.',
      error?.error ?? 'error',
    )
  }

  return parsed as T
}

async function tryRefresh(): Promise<boolean> {
  const refreshToken = tokens.refresh
  if (!refreshToken) return false
  try {
    const result = await request<{ accessToken: string; refreshToken: string }>(
      'POST',
      '/v1/auth/refresh',
      { refreshToken },
    )
    tokens.set(result.accessToken, result.refreshToken)
    return true
  } catch {
    tokens.clear()
    return false
  }
}

export interface Account {
  id: string
  email?: string
  displayName: string
  guest: boolean
  admin?: boolean
}

export interface WorldSummary {
  id: string
  name: string
  description: string
  tags: string[]
  rating: string
  anchoring: 'geo' | 'portable'
  instancing: 'personal' | 'shared'
  access: string
  headphoneHint: string
  pricing: {
    priceCents: number
    currency: string
    grantKind: string
    grantHours: number | null
    free: boolean
  }
  published?: boolean
  owned?: boolean
  entitled?: boolean
  placements?: PlacementRow[]
}

export interface PlacementRow {
  id: string
  name: string
  lat: number
  lon: number
  rotation_deg: number
  scale: number
  locked?: number
}

export interface AssetSummary {
  id: string
  name: string
  kind: string
  mime: string
  bytes: number
  duration_ms?: number | null
  transcript?: string | null
  url: string
}

export interface Bundle {
  world: WorldSummary
  version: number
  versionId: string
  definition: WorldDefinition
  placement: PlacementRow | null
  assets: { id: string; name: string; mime: string; bytes: number; transcript?: string | null; url: string }[]
  integrity: string
}

export interface JoinedSession {
  session: { id: string; worldId: string; mode: string; placementId: string | null; startedAt: number }
  participant: { id: string; roles: string[]; privacy: string }
  socketPath: string
}

export const api = {
  status: () => request<{ ok: boolean; inviteOnly: boolean }>('GET', '/v1/status'),
  authPolicy: () => request<{ inviteOnly: boolean; invitesPerMember: number }>('GET', '/v1/auth/policy'),

  async signUp(input: {
    email: string
    password: string
    displayName: string
    inviteCode?: string
  }): Promise<Account> {
    const result = await request<{ user: Account; accessToken: string; refreshToken: string }>(
      'POST',
      '/v1/auth/signup',
      input,
    )
    tokens.set(result.accessToken, result.refreshToken)
    return { ...result.user, guest: false }
  },

  async logIn(email: string, password: string): Promise<Account> {
    const result = await request<{ user: Account; accessToken: string; refreshToken: string }>(
      'POST',
      '/v1/auth/login',
      { email, password },
    )
    tokens.set(result.accessToken, result.refreshToken)
    return { ...result.user, guest: false }
  },

  async continueAsGuest(displayName = 'Guest'): Promise<Account> {
    const result = await request<{ user: Account; accessToken: string }>('POST', '/v1/auth/guest', {
      displayName,
    })
    tokens.set(result.accessToken)
    return { ...result.user, guest: true }
  },

  me: () => request<Account>('GET', '/v1/me'),
  logOut: () => tokens.clear(),

  createInvite: () => request<{ code: string; remaining: number | null }>('POST', '/v1/invites'),
  listInvites: () =>
    request<{ invites: { code: string; used: boolean }[]; remaining: number | null }>(
      'GET',
      '/v1/invites',
    ),
  joinWaitlist: (email: string) => request<{ ok: boolean }>('POST', '/v1/waitlist', { email }),

  listWorlds: () => request<{ worlds: WorldSummary[] }>('GET', '/v1/worlds'),
  getWorld: (id: string) => request<WorldSummary>('GET', `/v1/worlds/${id}`),
  createWorld: (input: { name: string; description?: string; anchoring?: string; instancing?: string }) =>
    request<WorldSummary>('POST', '/v1/worlds', input),
  updateWorld: (id: string, patch: Record<string, unknown>) =>
    request<WorldSummary>('PATCH', `/v1/worlds/${id}`, patch),

  getDraft: (id: string) => request<{ definition: WorldDefinition }>('GET', `/v1/worlds/${id}/draft`),
  saveDraft: (id: string, definition: WorldDefinition) =>
    request<{ issues: ValidationIssue[] }>('PUT', `/v1/worlds/${id}/draft`, { definition }),
  publish: (id: string, notes = '') =>
    request<{ version: number; issues: ValidationIssue[] }>('POST', `/v1/worlds/${id}/publish`, {
      notes,
    }),

  bundle: (id: string, placementId?: string) =>
    request<Bundle>(
      'GET',
      `/v1/worlds/${id}/bundle${placementId ? `?placementId=${placementId}` : ''}`,
    ),

  createPlacement: (
    worldId: string,
    input: { name?: string; lat: number; lon: number; rotationDeg?: number; scale?: number },
  ) => request<PlacementRow>('POST', `/v1/worlds/${worldId}/placements`, input),
  updatePlacement: (id: string, patch: Record<string, unknown>) =>
    request<PlacementRow>('PATCH', `/v1/placements/${id}`, patch),

  listAssets: (kind?: 'library' | 'personal') =>
    request<{ assets: AssetSummary[] }>('GET', `/v1/assets${kind ? `?kind=${kind}` : ''}`),
  uploadAsset: (
    file: Blob,
    input: { name: string; mime: string; kind?: 'library' | 'personal'; durationMs?: number },
  ) => {
    const query = new URLSearchParams({ name: input.name, mime: input.mime })
    if (input.kind) query.set('kind', input.kind)
    if (input.durationMs) query.set('durationMs', String(Math.round(input.durationMs)))
    return request<AssetSummary>('POST', `/v1/assets?${query}`, file, {
      headers: { 'content-type': input.mime },
    })
  },
  deleteAsset: (id: string) => request<void>('DELETE', `/v1/assets/${id}`),
  updateAsset: (id: string, patch: { name?: string; transcript?: string }) =>
    request<AssetSummary>('PATCH', `/v1/assets/${id}`, patch),

  joinSession: (worldId: string, input: { placementId?: string; displayName?: string; privacy?: string }) =>
    request<JoinedSession>('POST', `/v1/worlds/${worldId}/sessions`, input),
  liveSessions: (worldId: string) =>
    request<{ sessions: LiveSessionView[] }>('GET', `/v1/worlds/${worldId}/sessions`),
  sessionEvents: (sessionId: string, since = 0) =>
    request<{ events: SessionEventView[] }>(
      'GET',
      `/v1/sessions/${sessionId}/events?since=${since}`,
    ),
  spawnOneShot: (
    sessionId: string,
    input: {
      assetId: string
      busId?: string
      gainDb?: number
      position?: { x: number; y: number }
      participantId?: string
    },
  ) => request<{ ok: boolean }>('POST', `/v1/sessions/${sessionId}/spawn`, input),
  fireCue: (sessionId: string, cueId: string) =>
    request<{ ok: boolean }>('POST', `/v1/sessions/${sessionId}/cues/${cueId}`),
  endSession: (sessionId: string) => request<{ ok: boolean }>('POST', `/v1/sessions/${sessionId}/end`),
  trace: (sessionId: string) => request<TraceView>('GET', `/v1/sessions/${sessionId}/trace`),

  paymentsConfig: () => request<{ provider: string; testMode: boolean }>('GET', '/v1/payments/config'),
  startCheckout: (worldId: string) =>
    request<{ checkoutId: string; url: string; amountCents: number; currency: string; testMode: boolean }>(
      'POST',
      `/v1/worlds/${worldId}/checkout`,
    ),
  getCheckout: (id: string) =>
    request<{
      id: string
      worldId: string
      worldName: string
      status: string
      amountCents: number
      currency: string
      testMode: boolean
    }>('GET', `/v1/checkouts/${id}`),
  settleTestCheckout: (checkoutId: string, paid = true) =>
    request<{ ok: boolean; paid: boolean }>('POST', '/v1/payments/webhook', { checkoutId, paid }),
  myEntitlements: () => request<{ entitlements: unknown[] }>('GET', '/v1/me/entitlements'),
}

export interface ValidationIssue {
  severity: 'error' | 'warning'
  code: string
  message: string
  path: string
}

export interface SessionEventView {
  seq: number
  ts: number
  type: string
  scope: string
  participantId?: string
  ruleId?: string
  triggerType?: string
  cueId?: string
  emitterId?: string
  state?: string
  name?: string
  value?: unknown
  action?: { type: string; [key: string]: unknown }
}

export interface LiveSessionView {
  id: string
  worldId: string
  mode: string
  placementId: string | null
  startedAt: number
  participants: {
    id: string
    name: string
    roles: string[]
    privacy: string
    pose: { position: { x: number; y: number }; headingDeg?: number; accuracyM?: number } | null
    health: Record<string, unknown> | null
    joinedAt: number
  }[]
}

export interface TraceView {
  sessionId: string
  worldId: string
  poses: {
    participantId: string
    t: number
    position: { x: number; y: number }
    headingDeg: number | null
    speedMps: number | null
    accuracyM: number | null
    source: string
  }[]
  events: SessionEventView[]
}
